<template>
  <div id="dialog-curriculumHistory">
    <div class="curriculum-history">

      <div class="header">
        <p @click="windowOnOff(false)"><i class="ph-bold ph-arrow-left"></i></p>
        <h2>수강 현황</h2>
        <p style="opacity:0;"><i class="ph-bold ph-dots-three"></i></p>
      </div>

      <!-- 필터 탭 -->
      <div class="filter-tabs">
        <button 
          :class="{ active: filterTab === 'all' }"
          @click="filterTab = 'all'"
        >전체</button>
        <button 
          :class="{ active: filterTab === 'in_progress' }"
          @click="filterTab = 'in_progress'"
        >수강중</button>
        <button 
          :class="{ active: filterTab === 'completed' }"
          @click="filterTab = 'completed'"
        >완료</button>
      </div>

      <!-- 검색 -->
      <div class="search-box">
        <i class="ph ph-magnifying-glass"></i>
        <input 
          v-model="searchQuery" 
          type="text" 
          placeholder="회원 ID로 검색"
        />
      </div>

      <div class="content">
        <!-- 유저별 진행 현황 -->
        <div class="user-list">
          <div 
            v-for="user in filteredUsers" 
            :key="user.mb_id" 
            class="user-card"
            @click="toggleUserDetail(user.mb_id)"
          >
            <div class="user-summary">
              <div class="user-info">
                <div class="avatar">
                  <i class="ph ph-user"></i>
                </div>
                <div class="user-meta">
                  <span class="user-id">{{ user.mb_id }}</span>
                  <span class="last-access">최근 활동: {{ formatDate(user.last_accessed) }}</span>
                </div>
              </div>
              <div class="user-stats">
                <div class="stat-item">
                  <span class="stat-value">{{ user.completed_count }}</span>
                  <span class="stat-label">완료</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value">{{ user.in_progress_count }}</span>
                  <span class="stat-label">진행중</span>
                </div>
                <div class="stat-item progress-ring-container">
                  <svg class="progress-ring" width="44" height="44">
                    <circle class="progress-ring-bg" cx="22" cy="22" r="18" />
                    <circle 
                      class="progress-ring-fill" 
                      cx="22" cy="22" r="18"
                      :style="{ strokeDashoffset: getProgressOffset(user.overall_progress) }"
                    />
                  </svg>
                  <span class="progress-text">{{ user.overall_progress }}%</span>
                </div>
              </div>
              <i :class="expandedUser === user.mb_id ? 'ph ph-caret-up' : 'ph ph-caret-down'" class="expand-icon"></i>
            </div>

            <!-- 상세 레슨 진행 -->
            <div v-if="expandedUser === user.mb_id" class="user-detail" @click.stop>
              <div class="detail-header">
                <h4>레슨별 진행 현황</h4>
              </div>
              <div class="lesson-progress-list">
                <div 
                  v-for="lesson in user.lessons" 
                  :key="lesson.lesson_idx"
                  class="lesson-progress-item"
                >
                  <div class="lesson-info">
                    <i :class="lesson.content_type === 'video' ? 'ph ph-play-circle' : 'ph ph-article'"></i>
                    <div class="lesson-meta">
                      <span class="lesson-title">{{ lesson.title }}</span>
                      <span class="lesson-path">{{ lesson.category_title }} > {{ lesson.chapter_title }}</span>
                    </div>
                  </div>
                  <div class="lesson-status">
                    <div class="progress-bar-mini">
                      <div class="progress-fill" :style="{ width: lesson.progress + '%' }"></div>
                    </div>
                    <span :class="['status-text', lesson.status]">
                      {{ statusLabels[lesson.status] }}
                    </span>
                  </div>
                </div>
                <div v-if="user.lessons.length === 0" class="no-lessons">
                  수강 기록이 없습니다
                </div>
              </div>
            </div>
          </div>

          <!-- 빈 상태 -->
          <div v-if="filteredUsers.length === 0" class="empty-state">
            <i class="ph ph-users"></i>
            <p>{{ searchQuery ? '검색 결과가 없습니다' : '수강 기록이 없습니다' }}</p>
          </div>
        </div>

        <!-- 통계 요약 -->
        <div class="stats-summary">
          <div class="summary-card">
            <i class="ph ph-users"></i>
            <div class="summary-content">
              <span class="summary-value">{{ totalUsers }}</span>
              <span class="summary-label">총 수강자</span>
            </div>
          </div>
          <div class="summary-card">
            <i class="ph ph-check-circle"></i>
            <div class="summary-content">
              <span class="summary-value">{{ totalCompleted }}</span>
              <span class="summary-label">완료 레슨</span>
            </div>
          </div>
          <div class="summary-card">
            <i class="ph ph-clock"></i>
            <div class="summary-content">
              <span class="summary-value">{{ avgProgress }}%</span>
              <span class="summary-label">평균 진도</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import { inject, ref, computed, onMounted } from "vue"
import { useBackButton } from '@/utils/useBackBtn'

export default {
  props: {
    windowOnOff: Function,
  },
  setup(props) {
    const sql_query = inject('sql_query')

    useBackButton(() => {
      props.windowOnOff(false)
    })

    const userProgressList = ref([])
    const filterTab = ref('all')
    const searchQuery = ref('')
    const expandedUser = ref(null)

    const statusLabels = {
      not_started: '시작전',
      in_progress: '진행중',
      completed: '완료'
    }

    async function fetchData() {
      const progressData = await sql_query(`
        SELECT 
          p.mb_id,
          p.lesson_idx,
          p.status,
          p.progress,
          p.updated_at,
          l.title,
          l.content_type,
          ch.title as chapter_title,
          cat.title as category_title
        FROM user_lesson_progress p
        JOIN curriculum_items l ON p.lesson_idx = l.idx
        LEFT JOIN curriculum_items ch ON l.parent_idx = ch.idx
        LEFT JOIN curriculum_items cat ON ch.parent_idx = cat.idx
        ORDER BY p.mb_id, p.updated_at DESC
      `)

      const userMap = {}
      
      progressData.forEach(row => {
        if (!userMap[row.mb_id]) {
          userMap[row.mb_id] = {
            mb_id: row.mb_id,
            lessons: [],
            completed_count: 0,
            in_progress_count: 0,
            last_accessed: null,
            overall_progress: 0
          }
        }

        const user = userMap[row.mb_id]
        user.lessons.push(row)

        if (row.status === 'completed') user.completed_count++
        if (row.status === 'in_progress') user.in_progress_count++

        if (!user.last_accessed || new Date(row.updated_at) > new Date(user.last_accessed)) {
          user.last_accessed = row.updated_at
        }
      })

      const totalLessons = await sql_query(`
        SELECT COUNT(*) as cnt FROM curriculum_items WHERE type = 'lesson' AND is_active = 1
      `)
      const lessonCount = totalLessons[0]?.cnt || 1

      Object.values(userMap).forEach(user => {
        user.overall_progress = Math.round((user.completed_count / lessonCount) * 100)
      })

      userProgressList.value = Object.values(userMap)
    }

    const filteredUsers = computed(() => {
      let result = userProgressList.value

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(u => u.mb_id.toLowerCase().includes(query))
      }

      if (filterTab.value === 'in_progress') {
        result = result.filter(u => u.in_progress_count > 0)
      } else if (filterTab.value === 'completed') {
        result = result.filter(u => u.overall_progress === 100)
      }

      return result
    })

    const totalUsers = computed(() => userProgressList.value.length)
    
    const totalCompleted = computed(() => 
      userProgressList.value.reduce((sum, u) => sum + u.completed_count, 0)
    )

    const avgProgress = computed(() => {
      if (userProgressList.value.length === 0) return 0
      const sum = userProgressList.value.reduce((s, u) => s + u.overall_progress, 0)
      return Math.round(sum / userProgressList.value.length)
    })

    function toggleUserDetail(mbId) {
      expandedUser.value = expandedUser.value === mbId ? null : mbId
    }

    function formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '방금 전'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}일 전`
      
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    function getProgressOffset(progress) {
      const circumference = 2 * Math.PI * 18
      return circumference - (progress / 100) * circumference
    }

    onMounted(() => {
      fetchData()
    })

    return {
      filterTab,
      searchQuery,
      filteredUsers,
      expandedUser,
      toggleUserDetail,
      formatDate,
      getProgressOffset,
      statusLabels,
      totalUsers,
      totalCompleted,
      avgProgress
    }
  }
}
</script>


<style scoped>
#dialog-curriculumHistory {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  background-color: var(--color-background);
  z-index: var(--z-index-middle);
}

.curriculum-history {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.curriculum-history > .header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  color: var(--color-text-primary);
  padding: 16px;
  box-sizing: border-box;
}

.curriculum-history > .header > p {
  aspect-ratio: 1/1;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  margin: 0;
  padding: 0;
}

.curriculum-history > .header > h2 {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
}

.filter-tabs {
  display: flex;
  gap: 8px;
  padding: 0 16px;
  width: 100%;
  max-width: 800px;
  box-sizing: border-box;
}

.filter-tabs button {
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: var(--color-background-light);
  color: var(--color-text-secondary);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;
}

.filter-tabs button.active {
  background: var(--color-brand);
  color: var(--color-text-white);
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin: 16px;
  background: var(--color-background-light);
  border-radius: var(--radius-md);
  width: calc(100% - 32px);
  max-width: 768px;
  box-sizing: border-box;
}

.search-box i {
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
}

.search-box input {
  flex: 1;
  background: none;
  border: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  outline: none;
}

.content {
  width: 100%;
  max-width: 800px;
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-card {
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.user-card:hover {
  transform: translateY(-2px);
}

.user-summary {
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--color-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-white);
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

.user-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-id {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.last-access {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.user-stats {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 40px;
}

.stat-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.stat-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.progress-ring-container {
  position: relative;
  width: 44px;
  height: 44px;
}

.progress-ring {
  transform: rotate(-90deg);
}

.progress-ring-bg {
  fill: none;
  stroke: var(--color-background);
  stroke-width: 4;
}

.progress-ring-fill {
  fill: none;
  stroke: var(--color-brand);
  stroke-width: 4;
  stroke-linecap: round;
  stroke-dasharray: 113.1;
  transition: stroke-dashoffset 0.3s ease;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.expand-icon {
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
}

.user-detail {
  border-top: 1px solid var(--color-background);
  padding: 16px;
  cursor: default;
}

.detail-header h4 {
  margin: 0 0 12px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.lesson-progress-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.lesson-progress-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: var(--color-background);
  border-radius: var(--radius-md);
  gap: 12px;
}

.lesson-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.lesson-info > i {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

.lesson-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.lesson-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lesson-path {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.lesson-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar-mini {
  width: 60px;
  height: 4px;
  background: var(--color-background-light);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--color-brand);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.status-text {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.status-text.completed {
  color: #22c55e;
}

.status-text.in_progress {
  color: var(--color-brand);
}

.status-text.not_started {
  color: var(--color-text-secondary);
}

.no-lessons {
  text-align: center;
  padding: 20px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
}

.summary-card > i {
  font-size: var(--font-size-xl);
  color: var(--color-brand);
}

.summary-content {
  display: flex;
  flex-direction: column;
}

.summary-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.summary-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--color-text-secondary);
  gap: 12px;
}

.empty-state i {
  font-size: 48px;
  opacity: 0.5;
}

@media (max-width: 600px) {
  .user-stats {
    display: none;
  }
  
  .stats-summary {
    grid-template-columns: 1fr;
  }
}
</style>
